import * as prismic from '@prismicio/client'
import type { SummitSpeakerDirectoryItem } from '@/types/prismic'

export type { SummitSpeakerDirectoryItem } from '@/types/prismic'

/** Collapse CMS newlines/extra spaces so wrapping and matching stay stable. */
export function normalizeSummitSpeakerLine(text: string) {
  return text
    .replace(/\r?\n/g, ' ')
    .replace(/[ \t]+/g, ' ')
    .trim()
}

function normalizeForMatch(s: string) {
  return normalizeSummitSpeakerLine(s).toLowerCase()
}

/**
 * Split session roster lines ("Name - Org", "Name · Org", "Name, Org") for display and fuzzy matching.
 */
export function splitSummitSessionSpeakerLine(text: string): { name: string; rest: string } | null {
  const normalized = normalizeSummitSpeakerLine(text)
  const separators = [' - ', ' – ', ' — ', ' · ', ' • ', ', '] as const
  for (const sep of separators) {
    const idx = normalized.indexOf(sep)
    if (idx > 0) {
      const name = normalized.slice(0, idx).trim()
      const rest = normalized.slice(idx + sep.length).trim()
      if (name && rest) return { name, rest }
    }
  }
  return null
}

function speakerMatchStrings(speaker: SummitSpeakerDirectoryItem): string[] {
  const name = prismic.asText(speaker.name ?? []).trim()
  const designation = (speaker.designation ?? '').toString().trim()
  const keys = new Set<string>()
  const add = (s: string) => {
    const n = normalizeForMatch(s)
    if (n) keys.add(n)
  }
  if (name && designation) {
    add(`${name} - ${designation}`)
    add(`${name} · ${designation}`)
    add(`${name}, ${designation}`)
  }
  if (name) add(name)
  return [...keys]
}

/**
 * Map a session roster string (from `session_speakers_list`) to a Speakers slice row when copy aligns.
 */
export function findSpeakerForSummitLine(
  line: string,
  directory: SummitSpeakerDirectoryItem[],
): SummitSpeakerDirectoryItem | undefined {
  if (!line.trim() || directory.length === 0) return undefined

  const normLine = normalizeForMatch(line)
  const simplifiedLine = normLine.replace(/\([^)]*\)/g, '').replace(/\s+/g, ' ').trim()

  for (const speaker of directory) {
    for (const key of speakerMatchStrings(speaker)) {
      if (normLine === key || simplifiedLine === key) return speaker
    }
  }

  for (const speaker of directory) {
    for (const key of speakerMatchStrings(speaker)) {
      if (key.length >= 8 && (normLine.includes(key) || key.includes(normLine))) {
        return speaker
      }
    }
  }

  const parsed = splitSummitSessionSpeakerLine(line)
  if (parsed) {
    const nameNorm = normalizeForMatch(parsed.name)
    for (const speaker of directory) {
      const spName = normalizeForMatch(prismic.asText(speaker.name ?? []))
      if (!spName) continue
      if (spName === nameNorm || nameNorm === spName) return speaker
      if (spName.includes(nameNorm) || nameNorm.includes(spName)) return speaker
    }
  }

  return undefined
}
